import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useEffect, Suspense, lazy } from 'react'
import { Toaster } from 'react-hot-toast'
import ConfirmModal, { PromptModal } from './components/ConfirmModal'
import { useAuthStore } from './stores/authStore'
import Layout from './components/Layout'
import Login from './pages/Login'
import QRLogin from './pages/QRLogin'
import PageLoader from './components/PageLoader'
import ScanInfoPage from './pages/ScanInfoPage'
import NotFound from './pages/NotFound'
import MobileReLoginModal from './components/mobile/MobileReLoginModal'
import MobileAuthGuard from './components/mobile/MobileAuthGuard'
import { isMobileDevice } from './utils/deviceDetect'
// CameraTest: 개발 전용 디버그 페이지 — lazy + DEV 라우트만 노출
const CameraTest = lazy(() => import('./pages/mobile/CameraTest'))
// 별도 팝업 창 이미지 뷰어 (2026-08-18) — Layout 없음, 인증 불필요 (이미지 URL payload)
const ImageViewerWindow = lazy(() => import('./pages/ImageViewerWindow'))
import { MobileQRLogin, MobileQRScan } from './routes/lazyRoutes'

// 도메인별 분리된 라우트 배열
import { mobileRoutes } from './routes/mobileRoutes'
import { qrRoutes } from './routes/qrRoutes'
import { pcRoutes } from './routes/pcRoutes'

function PeriodicAlias() {
  const location = useLocation()
  return <Navigate to={`/inspection/periodic${location.search || ''}`} replace />
}

function DailyAlias() {
  const location = useLocation()
  return <Navigate to={`/checklist/daily${location.search || ''}`} replace />
}

// 인증된 모바일 사용자 → /mobile/home 리다이렉트
function MobileRedirectRoot() {
  if (isMobileDevice()) {
    return <Navigate to="/mobile/home" replace />
  }
  return <Layout />
}

function App() {
  const { isAuthenticated, initialize } = useAuthStore()

  useEffect(() => {
    initialize()
  }, [initialize])

  return (
    <Suspense fallback={<PageLoader />}>
    {/* 전역 토스트 */}
    <Toaster
      position="top-center"
      toastOptions={{
        duration: 3000,
        success: { style: { background: '#10b981', color: '#fff' } },
        error: { style: { background: '#ef4444', color: '#fff' } },
      }}
    />
    {/* 전역 confirm 모달 (window.confirm 대체) */}
    <ConfirmModal />
    <PromptModal />
    {/* 모바일 401 세션만료 시 인라인 재로그인 모달 */}
    <MobileReLoginModal />
    <Routes>
      {/* ═══ Public routes ═══ */}
      <Route path="/login" element={<Login />} />
      <Route path="/qr-login" element={<QRLogin />} />
      <Route path="/scan-info" element={<ScanInfoPage />} />
      <Route path="/image-viewer" element={<Suspense fallback={<PageLoader />}><ImageViewerWindow /></Suspense>} />
      {/* 레거시 /repair-request → /repair-request-form 리다이렉트 */}
      <Route path="/repair-request" element={<Navigate to="/repair-request-form" replace />} />
      <Route path="/periodic-inspection" element={<PeriodicAlias />} />
      <Route path="/daily-checklist" element={<DailyAlias />} />

      {/* ═══ QR 스캔 앱 지원 (네이버 등) ═══ */}
      <Route path="/m/qr/:qrCode" element={<MobileQRScan />} />
      <Route path="/m/qr" element={<MobileQRScan />} />
      <Route path="/qr/:qrCode" element={<MobileQRScan />} />

      {/* ═══ Mobile 공개 라우트 ═══ */}
      <Route path="/mobile/qr-login" element={<MobileQRLogin />} />
      {import.meta.env.DEV && (
        <Route path="/mobile/camera-test" element={<CameraTest />} />
      )}

      {/* ═══ Mobile 인증 필요 라우트 (→ routes/mobileRoutes.jsx) ═══ */}
      <Route element={<MobileAuthGuard />}>
        {mobileRoutes}
        {/* 없는 모바일 주소 — 빈 화면 대신 안내 (아래 PC catch-all 보다 먼저 잡는다) */}
        <Route path="/mobile/*" element={<NotFound />} />
      </Route>

      {/* ═══ QR Scan & Inspection (→ routes/qrRoutes.jsx) ═══ */}
      {qrRoutes}

      {/* ═══ PC 라우트 (→ routes/pcRoutes.jsx) ═══ */}
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <MobileRedirectRoot />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      >
        {pcRoutes}
        {/*
          라우트에 없는 주소를 **빈 화면으로 두지 않는다.** 여기에 두면 Layout 안에서
          그려져 상단 메뉴가 남아 있고, 로그인 전이면 부모가 /login 으로 보낸다.
        */}
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </Suspense>
  )
}

export default App
